import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { bookmarkSchema, type AgendaItem, type Bookmark } from "./schemas";
import type { ContentState } from "./use-event-content";

interface BookmarksResult {
  state: ContentState<Bookmark>;
  pendingIds: ReadonlySet<string>;
  mutationError: string | null;
  toggle: (item: AgendaItem) => Promise<void>;
}

/**
 * P12: the signed-in user's event-scoped bookmarks, kept live because this is
 * event-day state. Bookmark documents are immutable under the rules: toggling
 * removes an existing document or creates a fresh exact projection.
 */
export function useBookmarks(uid: string, eventId: string): BookmarksResult {
  const [state, setState] = useState<ContentState<Bookmark>>({
    status: "loading",
  });
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  const [mutationError, setMutationError] = useState<string | null>(null);

  useEffect(
    () =>
      onSnapshot(
        query(
          collection(db, "users", uid, "bookmarks"),
          where("eventId", "==", eventId),
          orderBy("startsAt"),
        ),
        (snapshot) => {
          const bookmarks: Bookmark[] = [];
          snapshot.forEach((bookmark) => {
            const parsed = bookmarkSchema.safeParse(bookmark.data());
            if (parsed.success) bookmarks.push(parsed.data);
          });
          setState({ status: "success", data: bookmarks });
        },
        (error) =>
          setState({
            status: "error",
            error:
              error instanceof Error
                ? error.message
                : "Failed to load bookmarks",
          }),
      ),
    [eventId, uid],
  );

  const toggle = useCallback(
    async (item: AgendaItem) => {
      if (!item.startsAt) {
        setMutationError("This agenda item cannot be bookmarked yet.");
        return;
      }

      setMutationError(null);
      setPendingIds((current) => new Set(current).add(item.id));

      try {
        const bookmarkRef = doc(db, "users", uid, "bookmarks", item.id);
        const isBookmarked =
          state.status === "success" &&
          state.data.some((bookmark) => bookmark.agendaItemId === item.id);

        if (isBookmarked) {
          await deleteDoc(bookmarkRef);
        } else {
          await setDoc(bookmarkRef, {
            eventId,
            agendaItemId: item.id,
            startsAt: item.startsAt,
            title: item.title,
            createdAt: serverTimestamp(),
          });
        }
      } catch (error) {
        setMutationError(
          error instanceof Error ? error.message : "Failed to update bookmark",
        );
      } finally {
        setPendingIds((current) => {
          const next = new Set(current);
          next.delete(item.id);
          return next;
        });
      }
    },
    [eventId, state, uid],
  );

  return { state, pendingIds, mutationError, toggle };
}
